import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  alpha,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Event as EventIcon,
  Public as PublicIcon,
  LocationCity as LocationCityIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Holiday, HolidayFormData, HolidayFilters } from '../types';
import holidayService from '../services/holidayService';
import HolidayForm from '../components/forms/HolidayForm';
import { useSnackbar } from 'notistack';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

interface HolidaysState {
  holidays: Holiday[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

const initialState: HolidaysState = {
  holidays: [],
  loading: false,
  error: null,
  total: 0,
  page: 0,
  rowsPerPage: 10,
  sortBy: 'dataInicio',
  sortOrder: 'ASC',
};

const initialFilters: HolidayFilters = {
  search: '',
  tipo: '',
  dataInicio: '',
  dataFim: '',
};

const HolidaysPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [state, setState] = useState<HolidaysState>(initialState);
  const [filters, setFilters] = useState<HolidayFilters>(initialFilters);
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados do formulário
  const [formOpen, setFormOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  // Carregar feriados
  const loadHolidays = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const params = {
        page: state.page + 1, // API usa página 1-based
        limit: state.rowsPerPage,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        ...filters,
      };

      const response = await holidayService.getAllHolidays(params);

      setState(prev => ({
        ...prev,
        holidays: response.holidays,
        total: response.pagination.total,
        loading: false,
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Erro ao carregar feriados',
        holidays: [],
        total: 0,
      }));
      enqueueSnackbar('Erro ao carregar feriados', { variant: 'error' });
    }
  }, [state.page, state.rowsPerPage, state.sortBy, state.sortOrder, filters, enqueueSnackbar]);

  // Carregar dados quando componente montar ou dependências mudarem
  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Manipular mudança de página
  const handleChangePage = (_: unknown, newPage: number) => {
    setState(prev => ({ ...prev, page: newPage }));
  };

  // Manipular mudança de registros por página
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Manipular mudanças nos filtros
  const handleFilterChange = (field: keyof HolidayFilters) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    const value = event.target.value as string;
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    setState(prev => ({ ...prev, page: 0 })); // Reset página
    loadHolidays();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters(initialFilters);
    setState(prev => ({ ...prev, page: 0 }));
  };

  // Abrir formulário para novo feriado
  const handleCreateHoliday = () => {
    setEditingHoliday(null);
    setFormOpen(true);
  };

  // Abrir formulário para editar feriado
  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormOpen(true);
  };

  // Fechar formulário
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingHoliday(null);
  };

  // Submeter formulário
  const handleSubmitForm = async (data: HolidayFormData) => {
    setFormLoading(true);
    try {
      if (editingHoliday) {
        // Editar
        await holidayService.updateHoliday(editingHoliday.id, data);
        enqueueSnackbar('Feriado atualizado com sucesso', { variant: 'success' });
      } else {
        // Criar
        await holidayService.createHoliday(data);
        enqueueSnackbar('Feriado criado com sucesso', { variant: 'success' });
      }
      
      loadHolidays(); // Recarregar lista
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
      throw error; // Propagar erro para o formulário
    } finally {
      setFormLoading(false);
    }
  };

  // Excluir feriado
  const handleDeleteHoliday = async (holiday: Holiday) => {
    if (!window.confirm(`Tem certeza que deseja excluir o feriado "${holiday.descricao}"?`)) {
      return;
    }

    try {
      await holidayService.deleteHoliday(holiday.id);
      enqueueSnackbar('Feriado excluído com sucesso', { variant: 'success' });
      loadHolidays(); // Recarregar lista
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  // Formatar data para exibição
  const formatDateTime = (dateString: string): string => {
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  // Obter cor do chip por tipo
  const getTypeChipColor = (tipo: string) => {
    switch (tipo) {
      case 'nacional':
        return 'primary';
      case 'estadual':
        return 'secondary';
      case 'municipal':
        return 'default';
      default:
        return 'default';
    }
  };

  // Verificar se há filtros ativos
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const countByTipo = (tipo: string) => state.holidays.filter(h => h.tipo === tipo).length;

  return ( 
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'warning')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <EventIcon sx={{ fontSize: 120, color: theme.palette.warning.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.35)}` }}>
              <EventIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gerenciamento de Feriados</Typography>
              <Typography variant="caption" color="text.secondary">Configure feriados nacionais, estaduais e municipais</Typography>
            </Box>
          </Stack>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreateHoliday} fullWidth={isMobile} sx={{ minWidth: { md: 140 } }}>
            Novo Feriado
          </Button>
        </Stack>
      </Paper>

      {!state.loading && state.total > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total" valor={state.total} icone={<EventIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Nacionais" valor={countByTipo('nacional')} subtitulo="nesta página" icone={<FlagIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Estaduais" valor={countByTipo('estadual')} subtitulo="nesta página" icone={<PublicIcon sx={{ color: theme.palette.secondary.main }} />} cor={theme.palette.secondary.main} bgCor={alpha(theme.palette.secondary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Municipais" valor={countByTipo('municipal')} subtitulo="nesta página" icone={<LocationCityIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
          <FilterIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
          {hasActiveFilters && <Chip size="small" label={`${Object.values(filters).filter(v => v !== '').length} ativo(s)`} color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />}
        </Stack>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por descrição..."
              value={filters.search}
              onChange={handleFilterChange('search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<FilterIcon />} onClick={() => setShowFilters(!showFilters)} color={hasActiveFilters ? 'primary' : 'inherit'}>
                Avançados
              </Button>
              <Button variant="contained" size="small" onClick={handleApplyFilters}>Buscar</Button>
            </Stack>
          </Grid>
        </Grid>

        {showFilters && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select value={filters.tipo} label="Tipo" onChange={handleFilterChange('tipo')}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="nacional">Nacional</MenuItem>
                    <MenuItem value="estadual">Estadual</MenuItem>
                    <MenuItem value="municipal">Municipal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="Data Início (de)" type="datetime-local" value={filters.dataInicio} onChange={handleFilterChange('dataInicio')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth size="small" label="Data Fim (até)" type="datetime-local" value={filters.dataFim} onChange={handleFilterChange('dataFim')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={handleClearFilters} fullWidth>Limpar</Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Feriados</Typography>
          <Chip size="small" label={`${state.total} registro${state.total !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>

        {state.error && (
          <Alert severity="error" sx={{ m: 1.5, fontSize: '0.8rem' }}>{state.error}</Alert>
        )}

        {state.loading ? (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando feriados...</Typography>
          </Box>
        ) : state.holidays.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <EventIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum feriado encontrado</Typography>
            <Typography variant="caption" color="text.secondary">
              {hasActiveFilters ? 'Tente ajustar os filtros de busca' : 'Clique em "Novo Feriado" para criar o primeiro'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={rankingTableHeadRowSx(theme)}>
                <TableCell>Descrição</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Data de Início</TableCell>
                <TableCell>Data de Fim</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.holidays.map((holiday) => (
                <TableRow key={holiday.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                          {holiday.descricao}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={holiday.tipo.charAt(0).toUpperCase() + holiday.tipo.slice(1)}
                          color={getTypeChipColor(holiday.tipo) as any}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatDateTime(holiday.dataInicio)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {formatDateTime(holiday.dataFim)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEditHoliday(holiday)}
                              color="primary"
                              sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteHoliday(holiday)}
                              color="error"
                              sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Box sx={tablePaginationWrapperSx(theme)}>
          <TablePagination
            component="div"
            size="small"
            count={state.total}
            page={state.page}
            onPageChange={handleChangePage}
            rowsPerPage={state.rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas:"
            sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
          />
        </Box>
          </>
        )}
      </Paper>

      {/* Modal do formulário */}
      <HolidayForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        holiday={editingHoliday}
        loading={formLoading}
      />
    </Box>
  );
};

export default HolidaysPage; 